"use client";

import { cn } from "@/lib/utils";
import { ReactNode, useEffect, useRef, useState } from "react";
import ChevronDownIcon from "@heroicons/react/24/solid/ChevronDownIcon";
import ChevronUpIcon from "@heroicons/react/24/solid/ChevronUpIcon";

interface Props {
  title?: string;
  children: ReactNode;
  className?: string;
  setIsOpened?: (value: boolean) => void;
  parentClassName: string;
  dropdownClassName?: string;
  disabled?: boolean;
}

export default function Dropdown(props: Props) {
  const {
    title,
    children,
    className,
    setIsOpened,
    parentClassName,
    dropdownClassName,
    disabled,
  } = props;
  const [open, setOpen] = useState(false);

  const dropdownRef: any = useRef(null);

  useEffect(() => {
    document.addEventListener("click", (e) => {
      if (!e.composedPath().includes(dropdownRef?.current)) {
        setOpen(false);
      }
    });
  }, []);

  useEffect(() => {
    if (setIsOpened) {
      setIsOpened(open);
    }
  }, [open]);

  return (
    <div
      ref={dropdownRef}
      className={cn(
        parentClassName,
        "relative h-[56px] text-assistant hover:text-primary sm:min-w-[269px] cursor-pointer group",
        open && "!text-primary",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        disabled && "hover:text-assistant",
      )}
    >
      <div
        onClick={() => setOpen(!open)}
        className={cn(
          "flex absolute z-20 gap-2 px-4 py-[15px] rounded-2xl border border-slate-300",
          "group-hover:border-secondary duration-200 bg-white items-center text-sm font-bold w-full justify-between h-[56px]",
          className,
          open && "!border-secondary shadow-md",
          disabled && "group-hover:border-slate-300 pointer-events-none",
        )}
      >
        <div className="px-2">
          <span className="h-8 flex justify-center items-center">{title}</span>
        </div>

        <div className="flex gap-2 items-center">
          <div className="h-8 w-[1px] bg-assistant-secondary" />

          {open ? (
            <ChevronUpIcon className="size-6" />
          ) : (
            <ChevronDownIcon className="size-6" />
          )}
        </div>
      </div>

      {!disabled && open && (
        <div
          onClick={() => {
            setTimeout(() => {
              setOpen(false);
            }, 50);
          }}
          className={cn(
            "flex items-start flex-col absolute shadow-2xl max-h-[258px] pt-[58px] overflow-y-auto px-6 top-[16px] none-scrollbar z-10 left-0 w-full rounded-2xl bg-white",
            parentClassName,
            dropdownClassName,
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}
